/**
 * API Client Helper Functions
 * Extracted to reduce complexity and lines per function in api-client.ts
 */

import { AUTH_ORIGIN } from '../utils/domain';

import type { ApiError } from './api-types';

interface ErrorResponseBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}


const AUTH_LOGIN_URL = `${AUTH_ORIGIN}/login`;

/**
 * Try to parse response body as JSON first, then fall back to plain text.
 * Extracted to reduce complexity in parseError.
 */
// eslint-disable-next-line complexity -- tries JSON then text with HTML detection; branches are inherent to format fallback
async function parseResponseBody(response: Response, cloned: Response): Promise<ApiError> {
  try {
    const body = await response.json() as ErrorResponseBody;
    return {
      code: body.code ?? `HTTP_${response.status}`,
      message: body.message ?? response.statusText,
      details: body.details,
    };
  } catch {
    // Response wasn't JSON — read as plain text (e.g. http.Error from Go)
    try {
      const text = (await cloned.text()).trim();
      if (text) {
        // Detect HTML responses (e.g. nginx 502 Bad Gateway) and extract a friendly message
        if (text.startsWith('<!') || text.startsWith('<html') || text.startsWith('<HTML')) {
          const titleMatch = /<title>(.*?)<\/title>/i.exec(text);
          return {
            code: `HTTP_${response.status}`,
            message: titleMatch?.[1] ?? `Server error (${response.status})`,
          };
        }
        return {
          code: `HTTP_${response.status}`,
          message: text,
        };
      }
    } catch { /* ignore */ }
    return {
      code: `HTTP_${response.status}`,
      message: response.statusText ?? 'Unknown error',
    };
  }
}

/**
 * Parse API error response.
 * Redirects to auth service if 401 Unauthorized or 403 Forbidden.
 */
export async function parseError(response: Response): Promise<ApiError> {
  // Handle 401 Unauthorized - redirect to auth service
  if (response.status === 401) {
    const returnUrl = encodeURIComponent(window.location.href);
    const authUrl = `${AUTH_LOGIN_URL}?return_url=${returnUrl}`;
    window.location.href = authUrl;
    // Return a placeholder error (won't be used as we're redirecting)
    return {
      code: 'UNAUTHORIZED',
      message: 'Redirecting to login...',
    };
  }

  // Handle 403 Forbidden - user doesn't have permission, redirect to auth
  if (response.status === 403) {
    const returnUrl = encodeURIComponent(window.location.href);
    const authUrl = `${AUTH_LOGIN_URL}?return_url=${returnUrl}&error=insufficient_permissions`;
    window.location.href = authUrl;
    // Return a placeholder error (won't be used as we're redirecting)
    return {
      code: 'FORBIDDEN',
      message: 'Insufficient permissions, redirecting...',
    };
  }

  // Clone response so we can try JSON first, then fall back to text
  const cloned = response.clone();
  return parseResponseBody(response, cloned);
}

/**
 * Handle network errors and create appropriate error response
 */
export function handleNetworkError(err: unknown): { error: ApiError } {
  if (err instanceof Error) {
    if (err.name === 'AbortError') {
      return {
        error: {
          code: 'TIMEOUT',
          message: 'Request timed out',
        },
      };
    }
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: err.message,
      },
    };
  }

  return {
    error: {
      code: 'UNKNOWN_ERROR',
      message: 'An unknown error occurred',
    },
  };
}

/**
 * Parse response data based on content type
 */
export async function parseResponseData<T>(response: Response): Promise<{ data?: T; error?: ApiError }> {
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return { data: undefined as T };
  }

  const data = await response.json() as T;
  return { data };
}
